import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import type { Run } from "@/app/gradingQueries";
import { isActive } from "@/app/gradingQueries";
import { Button } from "@/ui/Button";
import { blockerText, scoreTone, stripCells, type TaskState } from "./model";
import type { useSubmitFlow } from "./hooks";

const Eyebrow = ({ children, mb = "mb-3" }: { children: ReactNode; mb?: string }) => <div className={`${mb} font-mono text-[11px] uppercase tracking-[.06em] text-mut`}>{children}</div>;

/** Статус «на проверке» (жёлтый): точка + подпись, не только цвет; рядом —
 *  вход в S7 (id задания — из адреса /notebook/:id/check). */
export function SubmittedStatus() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const assignmentId = /^\/notebook\/([^/]+)/.exec(typeof path === "string" ? path : "")?.[1];
  return (
    <div className="flex flex-col gap-2">
      <div data-testid="submitted" role="status" className="flex items-center gap-2 rounded-sm border border-warn bg-transparent px-3 py-2.5 font-mono text-[12.5px]" style={{ color: "var(--warn)" }}>
        <span aria-hidden className="size-[7px] flex-none rounded-full" style={{ background: "var(--warn)" }} />
        Отправлено ментору · на проверке
      </div>
      {assignmentId && (
        <Link to="/notebook/$assignmentId/review" params={{ assignmentId }} className="font-mono text-[12px] text-acc underline-offset-2 hover:underline" data-testid="to-review">
          к ревью →
        </Link>
      )}
    </div>
  );
}

/** «отправка»: условия из eligibility, кнопка, причина и текст-блокер. */
export function SubmitCard({ flow, threshold, total, runN }: { flow: ReturnType<typeof useSubmitFlow>; threshold: number; total: number; runN?: number }) {
  const conds = flow.elig?.conditions ?? [];
  return (
    <section data-testid="submit-card" className="rounded-md border border-bd2 bg-card p-4">
      <Eyebrow>отправка</Eyebrow>
      {conds.length > 0 && (
        <div className="mb-3.5 flex flex-col gap-2.5">
          {conds.map((c) => (
            <div key={c.key} data-testid="condition" data-met={String(c.met)} className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5">
              <span className="font-mono text-[12px]" style={{ color: c.met ? "var(--ok)" : "var(--err)" }}>
                {c.met ? "✓" : "✕"}
              </span>
              <span className="text-[12.5px] leading-[1.5] text-pretty" style={{ color: c.met ? "var(--ink2)" : "var(--ink)" }}>
                {c.text}
              </span>
            </div>
          ))}
        </div>
      )}
      {flow.submitted ? (
        <SubmittedStatus />
      ) : (
        <>
          <Button variant="primary" className="w-full justify-center py-[11px]" disabled={!flow.eligible || flow.sending} onClick={() => void flow.send()} data-testid="submit">
            отправить на проверку
          </Button>
          {!flow.eligible && flow.reason && (
            <p data-testid="submit-reason" className="mb-0 mt-2.5 font-mono text-[11.5px] leading-[1.5] text-ink2">
              {flow.reason}
            </p>
          )}
          {flow.error && (
            <p data-testid="submit-error" role="alert" className="mb-0 mt-2.5 text-[12px] leading-[1.5]" style={{ color: "var(--err)" }}>
              {flow.error}
            </p>
          )}
          <p className="mb-0 mt-2.5 text-[12px] leading-[1.5] text-mut text-pretty">
            {flow.eligible ? `Условия выполнены — ментор получит прогон${runN ? ` #${runN}` : ""} вместе с ноутбуком.` : blockerText(threshold, total)}
          </p>
        </>
      )}
    </section>
  );
}

const CELL: Record<TaskState, { bg: string; bd: string }> = {
  idle: { bg: "transparent", bd: "var(--bd2)" },
  failed: { bg: "var(--err)", bd: "var(--err)" },
  passed: { bg: "var(--acc)", bd: "var(--acc)" },
};

function score(r: Run) {
  if (isActive(r)) return "идёт…";
  if (r.status === "error" || r.result?.env_error) return "ошибка";
  return `${r.passed}/${r.total}`;
}

/** «прогоны»: полоски по клетке на задачу, текущий выделен, клик переключает. */
export function RunsCard({ runs, current, threshold, onPick }: { runs: Run[]; current?: string; threshold: number; onPick: (id: string) => void }) {
  return (
    <section className="rounded-md border border-bd bg-card p-4">
      <Eyebrow>прогоны</Eyebrow>
      <div className="flex flex-col gap-1">
        {runs.map((r) => {
          const on = r.id === current;
          const err = r.status === "error" || !!r.result?.env_error;
          return (
            <button
              key={r.id}
              type="button"
              data-testid="run-row"
              aria-current={on ? "true" : undefined}
              aria-label={`прогон #${r.n}: ${score(r)}`}
              onClick={() => onPick(r.id)}
              className="grid cursor-pointer grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-sm bg-transparent px-1.5 py-[5px] text-left"
              style={{ border: `1px solid ${on ? "var(--acc)" : "transparent"}`, color: "var(--ink)" }}
            >
              <span className="font-mono text-[11.5px]" style={{ color: on ? "var(--ink)" : "var(--mut)" }}>
                #{r.n}
              </span>
              <span className="flex gap-[2px]">
                {stripCells(r, r.total).map((st, i) => (
                  <span key={i} data-cell={st} className="h-2 flex-1 rounded-[1px]" style={{ background: CELL[st].bg, border: `1px solid ${CELL[st].bd}` }} />
                ))}
              </span>
              <span className="font-mono text-[11px]" style={{ color: err ? "var(--err)" : scoreTone(r, threshold) }}>
                {score(r)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mb-0 mt-3 text-[12px] leading-[1.5] text-mut">Ментор увидит только отправленный прогон, но история остаётся у тебя.</p>
    </section>
  );
}

/** «что произойдёт после»: пункт 1 — номер прогона, который уйдёт ментору. */
export function AfterCard({ runN }: { runN?: number }) {
  const items = [
    `Сдача уходит в очередь ментора с результатами прогона${runN ? ` #${runN}` : ""}`,
    "Ментор комментирует конкретные ячейки, а не работу целиком",
    "Вердикт: «принято» или «нужны правки» с перечнем — итерации сохраняются",
  ];
  return (
    <section className="rounded-md border border-bd bg-card p-4" data-testid="after">
      <Eyebrow mb="mb-2.5">что произойдёт после</Eyebrow>
      <div className="flex flex-col gap-[9px]">
        {items.map((t, i) => (
          <div key={i} className="flex items-baseline gap-[9px] text-[12.5px] leading-[1.5] text-ink2">
            <span className="font-mono text-accdim">{i + 1}</span>
            <span className="text-pretty">{t}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
